import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Collapsible, Icon, SelectNative, fmtViewsCompact } from './ds';
import CampaignPills from './CampaignPills.jsx';
import { assignTagByLabel, removeTag } from '../api/tags.js';
import { updateLeadComment } from '../api/leads.js';
import { settleNoteSave } from '../lib/leadNote.mjs';
import { syncLeadStageOneCampaignMemberships, removeLeadFromStageOneCampaign } from '../api/outreach/campaigns.js';
import { screenDonors, fetchLatestDonorScreening } from '../api/donorScreening.js';
import {
    useLeadAccounts, useLeadAirtableLinks, createInAirtable, makeLeadOriginal,
    checkDuplicates, buildLeadWritePayload,
} from '../api/leads.js';
import DuplicateWarning from './DuplicateWarning.jsx';
import { PLATFORM_LABEL_SHORT, CONTACT_ONLY_PLATFORMS } from '../constants/platforms.js';
import { ReminderForm } from './ReminderForm.jsx';
import { TagPicker } from './TagPicker.jsx';
import { DuplicateSummary } from './shared/DuplicateSummary';
import { pickActiveCollab } from '../lib/activeCollab.js';
import { COLLAB_STATUS_LABELS } from '../lib/collaborationStatuses.js';
import {
    InstagramIcon, TikTokIcon, YouTubeIcon, FacebookIcon
} from './icons.jsx';
import { formatDateTime } from '../utils.js';
import { queryKeys } from '../lib/queryKeys';
import { usePacksByLead } from '../api/packs.js';
import PackCreateModal from './PackCreateModal.jsx';
import { accountRemovalErrorMessage } from '../lib/accountInUse.js';
import { usePackRounds } from '../api/rounds.js';
import { PACK_STATUS_LABEL, cpmTone, subStatusColor, subStatusLabel } from '../lib/packBadges.js';
import { buildProfileUrl } from '../lib/profileUrl.mjs';
import { HANDLE_LOCK_HINT, extraKey, lockedExtraKeys, lockedPrimaryPlatforms } from '../lib/accountHandleLock.mjs';

const formatCollabDate = (d) => {
    if (!d) return '—';
    const date = new Date(d);
    return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit' });
};

const DONOR_ERROR_TOOLTIPS = {
    rate_limit: 'RapidAPI лимит, попробуй позже',
    not_found:  'Аккаунт не найден',
    auth:       'Проблема с API',
    parse:      'Странный ответ от RapidAPI',
    transport:  'Сетевая ошибка',
    config:     'Неверная платформа',
};
const donorErrorTooltip = (kind) => DONOR_ERROR_TOOLTIPS[kind] || 'Ошибка';

const pluralRu = (n, forms) => {
    const abs = Math.abs(n);
    const mod10 = abs % 10;
    const mod100 = abs % 100;
    if (mod10 === 1 && mod100 !== 11) return forms[0];
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return forms[1];
    return forms[2];
};
const DAY_FORMS = ['день', 'дня', 'дней'];
const MONTH_FORMS = ['месяц', 'месяца', 'месяцев'];

const formatScreeningPeriod = (startIso, endIso) => {
    if (!startIso || !endIso) return null;
    const start = new Date(startIso).getTime();
    const end = new Date(endIso).getTime();
    if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return null;
    const diffDays = Math.max(1, Math.round((end - start) / 86_400_000));
    if (diffDays < 30) return `${diffDays} ${pluralRu(diffDays, DAY_FORMS)}`;
    const months = Math.round(diffDays / 30);
    return `${months} ${pluralRu(months, MONTH_FORMS)}`;
};

const formatRelativeRu = (iso) => {
    if (!iso) return 'никогда не проверялся';
    const ts = new Date(iso).getTime();
    if (!Number.isFinite(ts)) return 'никогда не проверялся';
    const diffMs = Date.now() - ts;
    if (diffMs < 60_000) return 'только что';
    const diffMin = Math.floor(diffMs / 60_000);
    if (diffMin < 60) return `${diffMin} мин назад`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr} ч назад`;
    const diffDays = Math.floor(diffHr / 24);
    if (diffDays === 0) return 'сегодня';
    if (diffDays < 30) return `${diffDays} ${pluralRu(diffDays, DAY_FORMS)} назад`;
    const months = Math.floor(diffDays / 30);
    return `${months} ${pluralRu(months, MONTH_FORMS)} назад`;
};

const ThreadsGlyph = () => <Icon name='threads-logo' size='sm' />;
const XGlyph = () => <Icon name='x-logo' size='sm' />;

const PLATFORM_ICONS = {
    instagram: InstagramIcon,
    tiktok:    TikTokIcon,
    youtube:   YouTubeIcon,
    facebook:  FacebookIcon,
    threads:   ThreadsGlyph,
    x:         XGlyph,
};
const PLATFORM_LINK_CLASS = {
    instagram: 'oc-lead-card__contact--ig',
    tiktok:    'oc-lead-card__contact--tt',
    youtube:   'oc-lead-card__contact--yt',
    facebook:  'oc-lead-card__contact--fb',
};

const mergeScreeningAccounts = (prev, next) => ({
    ...next,
    accounts: [
        ...(prev?.accounts ?? []).filter(a => !next.accounts.some(n => n.platform === a.platform)),
        ...next.accounts,
    ],
});

const fmtCpm = (v) => (v == null ? '—' : String(v).replace('.', ','));

function InfoPackRow({ pack, onOpenTab }) {
    const roundsQ = usePackRounds(pack?.id);
    const cpm = roundsQ.data?.rounds?.[0]?.economics?.cpm ?? null;
    const statusLabel = subStatusLabel(pack.subStatus)
        ?? PACK_STATUS_LABEL[pack.status]
        ?? pack.status;
    const accounts = Array.isArray(pack.accounts) ? pack.accounts : [];
    return (
        <div className='oc-lead-card__pack-row'>
            <Badge color={subStatusColor(pack.subStatus)} type='contained' size='small'>
                {statusLabel}
            </Badge>
            <span className='oc-lead-card__pack-accounts'>
                {accounts.map((a) => {
                    const platform = (a.platform || '').toLowerCase();
                    const Glyph = PLATFORM_ICONS[platform];
                    return (
                        <span key={a.id} className={`oc-lead-card__pack-handle ${PLATFORM_LINK_CLASS[platform] || ''}`}>
                            {Glyph ? <Glyph /> : null} {a.handle}
                        </span>
                    );
                })}
                {accounts.length === 0 && <span className='oc-lead-card__empty-text'>—</span>}
            </span>
            {pack.application && (
                <span className='oc-lead-card__chip'>{pack.application}</span>
            )}
            <span className='oc-lead-card__cpm'>CPM <b data-tone={cpmTone(cpm)}>{fmtCpm(cpm)}</b></span>
            <button
                type='button'
                className='oc-lead-card__pack-link'
                onClick={() => onOpenTab?.('monitoring', null, pack.id)}
            >
                Статистика →
            </button>
        </div>
    );
}

function LeadNote({ lead, queryClient }) {
    const saved = lead?.comment || '';
    const [draft, setDraft] = useState(saved);
    const [status, setStatus] = useState('idle');
    const dirtyRef = useRef(false);
    const draftRef = useRef(saved);
    const savingRef = useRef(false);

    useEffect(() => {
        if (!dirtyRef.current) {
            draftRef.current = saved;
            setDraft(saved);
        }
    }, [saved, lead?.id]);

    const commit = async () => {
        if (savingRef.current) return;
        savingRef.current = true;
        try {
            let resave = true;
            while (resave) {
                resave = false;
                const next = draftRef.current.trim();
                if (!dirtyRef.current) return;
                if (next === (queryClient.getQueryData(queryKeys.leads.detail(lead.id))?.comment ?? saved).trim()) {
                    dirtyRef.current = false;
                    return;
                }
                setStatus('saving');
                try {
                    const updated = await updateLeadComment(lead.id, next);
                    if (updated) queryClient.setQueryData(queryKeys.leads.detail(lead.id), updated);
                    queryClient.invalidateQueries({ queryKey: ['leads', 'column'] });
                    const outcome = settleNoteSave({ sent: next, storedComment: updated?.comment, currentDraft: draftRef.current });
                    if (outcome.clean) dirtyRef.current = false;
                    setStatus(outcome.status);
                    resave = outcome.resave;
                } catch {
                    setStatus('error');
                }
            }
        } finally {
            savingRef.current = false;
        }
    };

    return (
        <label className='oc-lead-card__form-field'>
            <span className='oc-lead-card__form-label'>
                Заметка
                {status === 'saving' && ' · сохраняю…'}
                {status === 'saved' && ' · Сохранено'}
                {status === 'error' && ' · не удалось сохранить'}
            </span>
            <textarea
                className='oc-lead-card__textarea'
                rows={3}
                value={draft}
                onChange={e => {
                    dirtyRef.current = true;
                    draftRef.current = e.target.value;
                    setDraft(e.target.value);
                    setStatus('idle');
                }}
                onBlur={commit}
                onKeyDown={e => {
                    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                        e.preventDefault();
                        commit();
                    }
                }}
                placeholder='Заметка по лиду…'
            />
        </label>
    );
}

export function LeadDetailsInfoTab({
    panelTab,
    lead,
    onClose,
    instagramProfileUrl,
    tiktokInfo,
    queryClient,
    leadCollabs,
    leadDuplicates,
    leadReminders,
    canUseOutreach = false,
    messagingOn = false,
    isSyncingCampaigns,
    setIsSyncingCampaigns,
    setFunnelEnrollModalOpen,
    onOpenTab,
    editMode = false,
    onSaveEdit,
    onCancelEdit,
    onEditSubmit,
    onFieldsSubmit,
}) {
    const [isCreatingInAirtable, setIsCreatingInAirtable] = useState(false);
    const [newTagLabel, setNewTagLabel] = useState('');

    const [open, setOpen] = useState({
        related: false, contacts: true, campaign: false, reminders: false, service: false,
    });
    const toggleBlock = (key) => setOpen(prev => ({ ...prev, [key]: !prev[key] }));

    const qc = useQueryClient();
    const screeningQuery = useQuery({
        // TanStack Query creates a cache entry for a disabled query as well; one fixed
        // key keeps every render before `lead.id` resolves in a single entry.
        queryKey: lead?.id
            ? queryKeys.donorScreening.detail(lead.id)
            : ['screenDonors', 'disabled'],
        queryFn: () => fetchLatestDonorScreening(lead.id),
        enabled: !!lead?.id,
        staleTime: 60_000,
        refetchOnWindowFocus: false,
        retry: false,
    });
    const screenMutationErrorMessage = (err) => {
        const code = err?.data?.code;
        if (code === 'NO_SOCIAL_ACCOUNT') return 'Лид не имеет ни одной соцсети';
        if (code === 'LEAD_NOT_FOUND')    return 'Лид не найден';
        if (code === 'FORBIDDEN')         return 'Нет доступа к этому лиду';
        if (code === 'RATE_WAIT')         return err.message;
        if (code === 'INVALID_PLATFORM' || code === 'PLATFORM_NOT_ON_LEAD') return 'Площадка не найдена у лида';
        return 'Не удалось запустить проверку';
    };
    const screenMutation = useMutation({
        mutationFn: ({ platform } = {}) => screenDonors(lead.id, platform),
        onSuccess: (data) => qc.setQueryData(queryKeys.donorScreening.detail(lead.id), (prev) => mergeScreeningAccounts(prev, data)),
        onError: () => { /* state lives in `screenMutation.isError/.error`; JSX banner below renders it. */ },
    });

    const { data: accountsEnvelope } = useLeadAccounts(lead?.id);

    const airLinksQ = useLeadAirtableLinks(lead?.id);
    const airLinks = airLinksQ.data ?? [];

    const packsQ = usePacksByLead(lead?.id);
    const packs = packsQ.data ?? [];
    const [createPackOpen, setCreatePackOpen] = useState(false);
    const accountsList = useMemo(
        () => (Array.isArray(accountsEnvelope?.accounts) ? accountsEnvelope.accounts : []),
        [accountsEnvelope],
    );

    const lockedPlatforms = useMemo(() => lockedPrimaryPlatforms(accountsList), [accountsList]);
    const lockedExtras = useMemo(() => lockedExtraKeys(accountsList), [accountsList]);

    const platforms = useMemo(() => {
        return accountsList
            .filter(a => a.isPrimary === true)
            .map(a => ({
                key: a.platform,
                handle: a.handle,
                profileUrl: a.profileUrl ?? buildProfileUrl(a.platform, a.handle),
            }));
    }, [accountsList]);

    const extraAccountsList = useMemo(() => {
        return accountsList
            .filter(a => a.isPrimary !== true && (a.accountPurpose === 'ambassador' || !a.accountPurpose))
            .map(a => ({ platform: a.platform, handle: a.handle, profileUrl: a.profileUrl, packId: a.packId, handleLocked: a.handleLocked === true }));
    }, [accountsList]);

    const hasAnySocial = platforms.length > 0;
    const hasCheckableSocial = platforms.some(p => !CONTACT_ONLY_PLATFORMS.includes(p.key));
    const screeningData = screeningQuery.data;
    const threshold = screeningData?.threshold ?? 5000;
    const screeningAccounts = Array.isArray(screeningData?.accounts) ? screeningData.accounts : [];
    const screenedAt = screeningData?.screenedAt;

    const currentIsOriginal = !lead?.duplicateOfId;
    const allRelated = useMemo(() => {
        const rows = [];
        if (lead?.duplicateOfId) {
            const orig = leadDuplicates.find(d => d.id === lead.duplicateOfId);
            if (orig) rows.push({ ...orig, _role: 'original' });
        }
        for (const dup of leadDuplicates) {
            if (dup.id !== lead?.duplicateOfId) {
                const isDupOriginal = !dup.isDuplicate && !dup.duplicateOfId;
                rows.push({ ...dup, _role: isDupOriginal ? 'original' : 'duplicate' });
            }
        }
        return rows;
    }, [lead?.duplicateOfId, leadDuplicates]);

    const [form, setForm] = useState(null);
    const [extraAccounts, setExtraAccounts] = useState([]);
    const [extrasDirty, setExtrasDirty] = useState(false);
    const [newAccPlatform, setNewAccPlatform] = useState('');
    const [newAccHandle, setNewAccHandle] = useState('');
    const [dupCheckResult, setDupCheckResult] = useState(null);
    const [dupChecking, setDupChecking] = useState(false);
    const [inlineSaving, setInlineSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const dupDebounceRef = useRef(null);
    const wasEditingRef = useRef(false);

    useEffect(() => {
        const entering = editMode && !wasEditingRef.current;
        const leaving = !editMode && wasEditingRef.current;
        wasEditingRef.current = editMode;
        if (entering) {
            const meta = lead?.metadata || {};
            setForm({
                name: lead?.name || '',
                realName: lead?.realName || '',
                email: lead?.email || '',
                phone: lead?.phone || '',
                telegramNick: meta.telegramNick || '',
                instagram: lead?.instagram || '',
                tiktok: lead?.tiktok || '',
                youtube: lead?.youtube || '',
                facebook: lead?.facebook || '',
                threads: lead?.threads || '',
                x: lead?.x || '',
                comment: lead?.comment || '',
                negotiationMin: meta.negotiationPriceMin ?? meta.negotiation?.min ?? '',
                negotiationMax: meta.negotiationPriceMax ?? meta.negotiation?.max ?? '',
            });
            setExtraAccounts(extraAccountsList.map(a => ({ platform: a.platform, handle: a.handle })));
            setExtrasDirty(false);
            setNewAccPlatform('');
            setNewAccHandle('');
            setSaveError(null);
            setOpen(prev => ({ ...prev, contacts: true, reminders: true, service: true }));
        }
        if (editMode && !entering && !extrasDirty) {
            setExtraAccounts(extraAccountsList.map(a => ({ platform: a.platform, handle: a.handle })));
        }
        if (entering || leaving) {
            if (dupDebounceRef.current) clearTimeout(dupDebounceRef.current);
            setDupCheckResult(null);
            setDupChecking(false);
        }
    }, [editMode, lead, extraAccountsList, extrasDirty]);

    const handleFormChange = (field, value) => {
        setForm(prev => (prev ? { ...prev, [field]: value } : prev));
    };

    const handleIdentifierChange = (field, value) => {
        handleFormChange(field, value);
        if (dupDebounceRef.current) clearTimeout(dupDebounceRef.current);
        dupDebounceRef.current = setTimeout(async () => {
            const current = { ...(form || {}), [field]: value };
            const { instagram, tiktok, email } = current;
            if (!instagram && !tiktok && !email) {
                setDupCheckResult(null);
                return;
            }
            setDupChecking(true);
            try {
                setDupCheckResult(await checkDuplicates({ instagram, tiktok, email, excludeId: lead?.id }));
            } catch (err) {
                console.error('Dedup check failed', err);
                setDupCheckResult(null);
            } finally {
                setDupChecking(false);
            }
        }, 500);
    };

    const addExtraAccount = () => {
        const handle = newAccHandle.trim();
        if (!newAccPlatform || !handle) return;
        setExtraAccounts(prev => [...prev, { platform: newAccPlatform, handle }]);
        setExtrasDirty(true);
        setNewAccPlatform('');
        setNewAccHandle('');
    };
    const removeExtraAccount = (index) => {
        setExtraAccounts(prev => prev.filter((_, j) => j !== index));
        setExtrasDirty(true);
    };

    const handleInlineSave = async () => {
        if (!form || inlineSaving) return;
        const meta = lead?.metadata || {};
        const payload = buildLeadWritePayload(form, extraAccounts, meta.payment ?? null);
        if (!extrasDirty) delete payload.extraAccounts;
        setInlineSaving(true);
        setSaveError(null);
        try {
            await onEditSubmit?.(payload);
            onSaveEdit?.();
        } catch (err) {
            setSaveError(err?.data?.error === 'invalid_handle'
                ? err.data.reason
                : accountRemovalErrorMessage(err) ?? 'Не удалось сохранить карточку.');
        } finally {
            setInlineSaving(false);
        }
    };

    const showEditForm = editMode && !!form;

    const contactChannels = [
        lead.email && 'email',
        lead.instagram && 'IG',
        tiktokInfo?.display && 'TT',
        lead.phone && 'тел.',
    ].filter(Boolean).join(' · ') || '—';

    const igProfile = lead?.metadata?.instagramProfile;

    return (
        <aside className='details-modal__info oc-lead-card__info' style={{ display: panelTab === 'info' ? 'flex' : 'none' }}>

            <section className='oc-lead-card__block'>
                <div className='oc-lead-card__row'>
                    <div className='oc-lead-card__sect-label'>Донорский аккаунт</div>
                    {hasCheckableSocial && screeningQuery.isSuccess && !screenedAt && (
                        <span className='oc-lead-card__muted'
                              title='Проверка запускается кнопкой «Проверить» на строке площадки'>
                            не проверен
                        </span>
                    )}
                </div>
                {!hasAnySocial ? (
                    <span className='oc-lead-card__empty-text'>Соцсети не указаны</span>
                ) : (
                    <div className='oc-lead-card__donor-lines'>
                        {platforms.map((p) => {
                            const Glyph = PLATFORM_ICONS[p.key];
                            const linkCls = PLATFORM_LINK_CLASS[p.key] || '';
                            const stat = screeningAccounts.find(a => a.platform === p.key);
                            const followers = stat && !stat.error ? stat.followers : null;
                            const avgViews = stat && typeof stat.avgViews === 'number' ? stat.avgViews : null;
                            const period = formatScreeningPeriod(stat?.periodStart, stat?.periodEnd);
                            const thresholdTitle = screenedAt
                                ? `Порог команды: ${threshold.toLocaleString('ru-RU')} среднее (на момент проверки)`
                                : `Порог команды: ${threshold.toLocaleString('ru-RU')} среднее`;
                            const rowTitle = stat && !stat.error
                                ? [
                                    `Постов в снимке: ${stat.totalReels ?? 0}`,
                                    period ? `период ${period}` : null,
                                    thresholdTitle,
                                ].filter(Boolean).join(' · ')
                                : undefined;
                            const isCheckingThis = screenMutation.isPending
                                && screenMutation.variables?.platform === p.key;
                            const failedHere = screenMutation.isError
                                && screenMutation.variables?.platform === p.key;
                            return (
                                <div key={p.key} className='oc-lead-card__donor-line' title={rowTitle}>
                                    {p.profileUrl ? (
                                        <a
                                            href={p.profileUrl}
                                            target='_blank'
                                            rel='noopener noreferrer'
                                            className={`oc-lead-card__contact ${linkCls}`}
                                            onClick={e => e.stopPropagation()}
                                        >
                                            {Glyph ? <Glyph /> : null} {p.handle}
                                        </a>
                                    ) : (
                                        <span className={`oc-lead-card__contact ${linkCls}`}>
                                            {Glyph ? <Glyph /> : null} {p.handle}
                                        </span>
                                    )}
                                    {followers != null && (
                                        <>
                                            <span className='oc-lead-card__donor-sep'>·</span>
                                            <span><b>{fmtViewsCompact(followers)}</b> подписчиков</span>
                                        </>
                                    )}
                                    {avgViews != null && (
                                        <>
                                            <span className='oc-lead-card__donor-sep'>·</span>
                                            <span>
                                                <b>{fmtViewsCompact(avgViews)}</b> ср. просмотры / 12 последних постов
                                                {' '}
                                                <Icon
                                                    name={stat.hasEnoughViews ? 'check-circle' : 'x-circle'}
                                                    size='sm'
                                                    color={stat.hasEnoughViews ? 'success' : 'error'}
                                                />
                                            </span>
                                        </>
                                    )}
                                    {stat && !stat.error && stat.avgViews === null && (
                                        <>
                                            <span className='oc-lead-card__donor-sep'>·</span>
                                            <span className='oc-lead-card__donor-stats' title='Нет данных для расчёта среднего'>
                                                нет данных <Icon name='warning-circle' size='sm' color='warning' />
                                            </span>
                                        </>
                                    )}
                                    {stat?.error && (
                                        <>
                                            <span className='oc-lead-card__donor-sep'>·</span>
                                            <span
                                                className='oc-lead-card__donor-error'
                                                title={donorErrorTooltip(stat.error.kind)}
                                            >
                                                <Icon name='x-circle' size='sm' color='error' />
                                                {' '}Не удалось получить данные
                                            </span>
                                        </>
                                    )}
                                    {failedHere && (
                                        <span className='oc-lead-card__donor-error' role='alert'>
                                            <Icon name='x-circle' size='sm' color='error' />
                                            {' '}{screenMutationErrorMessage(screenMutation.error)}
                                        </span>
                                    )}
                                    {!CONTACT_ONLY_PLATFORMS.includes(p.key) && (
                                    <Button
                                        hierarchy='tertiary'
                                        size='small'
                                        className='oc-lead-card__donor-check'
                                        onClick={() => { if (lead?.id) screenMutation.mutate({ platform: p.key }); }}
                                        disabled={screenMutation.isPending || !lead?.id}
                                        title={`Проверить показатели: ${PLATFORM_LABEL_SHORT[p.key] || p.key}`}
                                        leadingIcon={isCheckingThis
                                            ? <Icon name='circle-notch' size='sm' className='oc-lead-card__spinner' />
                                            : undefined}
                                    >
                                        {isCheckingThis ? 'Проверяем...' : 'Проверить'}
                                    </Button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
                {hasCheckableSocial && (
                    <div className='oc-lead-card__donor-footer'>
                        Последняя проверка: {formatRelativeRu(screenedAt)}
                    </div>
                )}
            </section>

            <section className='oc-lead-card__block'>
                <div className='oc-lead-card__row'>
                    <div className='oc-lead-card__sect-label'>Пачки</div>
                    <Button
                        hierarchy='tertiary'
                        size='small'
                        className='oc-lead-card__right'
                        disabled={!lead?.id}
                        onClick={() => setCreatePackOpen(true)}
                    >
                        + Пачка
                    </Button>
                </div>
                {packs.length === 0 ? (
                    <span className='oc-lead-card__empty-text'>Пачек нет</span>
                ) : (
                    <div className='oc-lead-card__pack-rows'>
                        {packs.map(p => (
                            <InfoPackRow key={p.id} pack={p} onOpenTab={onOpenTab} />
                        ))}
                    </div>
                )}
                {createPackOpen && lead?.id && (
                    <PackCreateModal leadId={lead.id} onClose={() => setCreatePackOpen(false)} />
                )}
            </section>

            <section className='oc-lead-card__block'>
                {showEditForm ? (
                    <label className='oc-lead-card__form-field'>
                        <span className='oc-lead-card__form-label'>Заметка</span>
                        <textarea
                            className='oc-lead-card__textarea'
                            rows={3}
                            value={form.comment}
                            onChange={e => handleFormChange('comment', e.target.value)}
                            disabled={inlineSaving}
                            placeholder='Заметка по лиду…'
                        />
                    </label>
                ) : (
                    <LeadNote lead={lead} queryClient={queryClient} />
                )}
            </section>

            <Collapsible
                id='oc-lead-card-related'
                open={open.related}
                onToggle={() => toggleBlock('related')}
                title='Связанные'
                count={allRelated.length}
                meta={currentIsOriginal
                    ? <span className='oc-lead-card__rel-note oc-lead-card__rel-note--original'>вы — оригинал</span>
                    : <span className='oc-lead-card__rel-note oc-lead-card__rel-note--duplicate'>это дубль</span>}
            >
                {allRelated.length > 0 ? (
                    <DuplicateSummary
                        variant='section'
                        related={allRelated}
                        sourceLead={{
                            instagram: lead?.instagram,
                            tiktok: lead?.tiktok,
                            email: lead?.email,
                        }}
                        onNavigate={(id) => {
                            onClose?.();
                            window.history.pushState({}, '', `?lead=${id}`);
                            window.dispatchEvent(new PopStateEvent('popstate'));
                        }}
                        onMakeOriginal={async (id) => {
                            if (!window.confirm('Сделать этот лид оригиналом блогера? Остальные станут дублями.')) return;
                            try {
                                await makeLeadOriginal(id, true);
                                qc.invalidateQueries({ queryKey: queryKeys.leads.detail(lead.id) });
                                qc.invalidateQueries({ queryKey: ['leads', 'bloggers'] });
                                qc.invalidateQueries({ queryKey: queryKeys.leads.all });
                            } catch (err) {
                                alert('Не удалось закрепить оригинал: ' + (err?.data?.error || err?.message || 'неизвестная ошибка'));
                            }
                        }}
                    />
                ) : (
                    <div className='oc-lead-card__rel-empty'>
                        <Icon name='crown-simple' size='sm' color='success' />
                        {' '}Дублей нет — это оригинальный аккаунт
                    </div>
                )}
            </Collapsible>

            <Collapsible
                id='oc-lead-card-contacts'
                open={open.contacts}
                onToggle={() => toggleBlock('contacts')}
                title='Контакты и связь'
                meta={<span className='oc-lead-card__acc-meta'>{contactChannels}</span>}
            >
                {showEditForm ? (
                    <>
                        <div className='oc-lead-card__form-grid'>
                            <label className='oc-lead-card__form-field'>
                                <span className='oc-lead-card__form-label'>Никнейм *</span>
                                <input
                                    className='oc-lead-card__input'
                                    type='text'
                                    value={form.name}
                                    onChange={e => handleFormChange('name', e.target.value)}
                                    disabled={inlineSaving}
                                    placeholder='@username'
                                />
                            </label>
                            <label className='oc-lead-card__form-field'>
                                <span className='oc-lead-card__form-label'>Имя</span>
                                <input
                                    className='oc-lead-card__input'
                                    type='text'
                                    value={form.realName}
                                    onChange={e => handleFormChange('realName', e.target.value)}
                                    disabled={inlineSaving}
                                    placeholder='Имя для обращения'
                                />
                                <span className='oc-lead-card__form-hint'>
                                    Имя инстаграм: {lead?.metadata?.igDisplayName ?? '—'}
                                </span>
                            </label>
                            <label className='oc-lead-card__form-field'>
                                <span className='oc-lead-card__form-label'>Email</span>
                                <input
                                    className='oc-lead-card__input'
                                    type='email'
                                    value={form.email}
                                    onChange={e => handleIdentifierChange('email', e.target.value)}
                                    disabled={inlineSaving}
                                    placeholder='email@example.com'
                                />
                            </label>
                            <label className='oc-lead-card__form-field'>
                                <span className='oc-lead-card__form-label'>Номер телефона</span>
                                <input
                                    className='oc-lead-card__input'
                                    type='text'
                                    value={form.phone}
                                    onChange={e => handleFormChange('phone', e.target.value)}
                                    disabled={inlineSaving}
                                    placeholder='+7 999 000 00 00'
                                />
                            </label>
                            <label className='oc-lead-card__form-field'>
                                <span className='oc-lead-card__form-label'>Ник Telegram</span>
                                <input
                                    className='oc-lead-card__input'
                                    type='text'
                                    value={form.telegramNick}
                                    onChange={e => handleFormChange('telegramNick', e.target.value)}
                                    disabled={inlineSaving}
                                    placeholder='@username'
                                />
                            </label>
                            <label className='oc-lead-card__form-field'>
                                <span className='oc-lead-card__form-label'>Instagram</span>
                                <input
                                    className='oc-lead-card__input'
                                    type='text'
                                    value={form.instagram}
                                    onChange={e => handleIdentifierChange('instagram', e.target.value)}
                                    disabled={inlineSaving || lockedPlatforms.has('instagram')}
                                    title={lockedPlatforms.has('instagram') ? HANDLE_LOCK_HINT : undefined}
                                    placeholder='@username'
                                />
                                {lockedPlatforms.has('instagram') && <span className='oc-lead-card__empty-text'>{HANDLE_LOCK_HINT}</span>}
                            </label>
                            <label className='oc-lead-card__form-field'>
                                <span className='oc-lead-card__form-label'>TikTok</span>
                                <input
                                    className='oc-lead-card__input'
                                    type='text'
                                    value={form.tiktok}
                                    onChange={e => handleIdentifierChange('tiktok', e.target.value)}
                                    disabled={inlineSaving || lockedPlatforms.has('tiktok')}
                                    title={lockedPlatforms.has('tiktok') ? HANDLE_LOCK_HINT : undefined}
                                    placeholder='@username'
                                />
                                {lockedPlatforms.has('tiktok') && <span className='oc-lead-card__empty-text'>{HANDLE_LOCK_HINT}</span>}
                            </label>
                            <label className='oc-lead-card__form-field'>
                                <span className='oc-lead-card__form-label'>YouTube</span>
                                <input
                                    className='oc-lead-card__input'
                                    type='text'
                                    value={form.youtube}
                                    onChange={e => handleFormChange('youtube', e.target.value)}
                                    disabled={inlineSaving || lockedPlatforms.has('youtube')}
                                    title={lockedPlatforms.has('youtube') ? HANDLE_LOCK_HINT : undefined}
                                    placeholder='Ссылка на канал'
                                />
                                {lockedPlatforms.has('youtube') && <span className='oc-lead-card__empty-text'>{HANDLE_LOCK_HINT}</span>}
                            </label>
                            <label className='oc-lead-card__form-field'>
                                <span className='oc-lead-card__form-label'>Facebook</span>
                                <input
                                    className='oc-lead-card__input'
                                    type='text'
                                    value={form.facebook}
                                    onChange={e => handleFormChange('facebook', e.target.value)}
                                    disabled={inlineSaving || lockedPlatforms.has('facebook')}
                                    title={lockedPlatforms.has('facebook') ? HANDLE_LOCK_HINT : undefined}
                                    placeholder='Ссылка на профиль'
                                />
                                {lockedPlatforms.has('facebook') && <span className='oc-lead-card__empty-text'>{HANDLE_LOCK_HINT}</span>}
                            </label>
                            <label className='oc-lead-card__form-field'>
                                <span className='oc-lead-card__form-label'>Threads</span>
                                <input
                                    className='oc-lead-card__input'
                                    type='text'
                                    value={form.threads}
                                    onChange={e => handleFormChange('threads', e.target.value)}
                                    disabled={inlineSaving}
                                    placeholder='Ссылка или @ник'
                                />
                            </label>
                            <label className='oc-lead-card__form-field'>
                                <span className='oc-lead-card__form-label'>X</span>
                                <input
                                    className='oc-lead-card__input'
                                    type='text'
                                    value={form.x}
                                    onChange={e => handleFormChange('x', e.target.value)}
                                    disabled={inlineSaving}
                                    placeholder='Ссылка или @ник'
                                />
                            </label>
                        </div>
                        <div className='oc-lead-card__dupcheck'>
                            <span className='oc-lead-card__form-label'>Проверка на уникальность</span>
                            {dupChecking && (
                                <span className='oc-lead-card__empty-text'>Проверяем...</span>
                            )}
                            {!dupChecking && dupCheckResult?.rejected?.length > 0 && (
                                <div className='oc-lead-card__error' role='alert'>
                                    {dupCheckResult.rejected.map((r) => (
                                        <div key={r.field}>{r.reason}</div>
                                    ))}
                                </div>
                            )}
                            {!dupChecking && dupCheckResult && !dupCheckResult.isDuplicate && !dupCheckResult.rejected?.length && (
                                <span className='oc-lead-card__dupcheck-ok'>
                                    <Icon name='check' size='sm' /> Дублей не найдено
                                </span>
                            )}
                            {!dupChecking && dupCheckResult && dupCheckResult.isDuplicate && (
                                <DuplicateWarning
                                    duplicates={dupCheckResult.duplicates}
                                    claimIds={new Set()}
                                    onToggleClaim={() => {}}
                                    onToggleAllClaims={() => {}}
                                    onClaim={() => {}}
                                    claiming={false}
                                    showRowColumn={false}
                                    compact
                                />
                            )}
                            {!dupChecking && !dupCheckResult && (
                                <span className='oc-lead-card__empty-text'>
                                    Заполните Instagram, TikTok или Email для проверки
                                </span>
                            )}
                        </div>
                    </>
                ) : (
                    <div className='oc-lead-card__contacts'>
                        {lead.instagram && (
                            <a href={instagramProfileUrl} target='_blank' rel='noopener noreferrer' className='oc-lead-card__contact oc-lead-card__contact--ig' onClick={e => e.stopPropagation()}>
                                <InstagramIcon /> {lead.instagram}
                            </a>
                        )}
                        {lead.email && (
                            <a href={`mailto:${lead.email}`} className='oc-lead-card__contact oc-lead-card__contact--email' onClick={e => e.stopPropagation()}>
                                <Icon name='envelope' size='sm' /> {lead.email}
                            </a>
                        )}
                        {tiktokInfo?.display && (
                            <a href={`https://www.tiktok.com/@${tiktokInfo.display.replace(/^@/, '')}`} target='_blank' rel='noopener noreferrer' className='oc-lead-card__contact oc-lead-card__contact--tt' onClick={e => e.stopPropagation()}>
                                <TikTokIcon /> {tiktokInfo.display}
                            </a>
                        )}
                        {lead.youtube && (
                            <a href={lead.youtube.startsWith('http') ? lead.youtube : `https://youtube.com/${lead.youtube}`} target='_blank' rel='noopener noreferrer' className='oc-lead-card__contact oc-lead-card__contact--yt' onClick={e => e.stopPropagation()}>
                                <YouTubeIcon /> {lead.youtube}
                            </a>
                        )}
                        {lead.phone && (
                            <a href={`tel:${lead.phone}`} className='oc-lead-card__contact oc-lead-card__contact--phone' onClick={e => e.stopPropagation()}>
                                {lead.phone}
                            </a>
                        )}
                        {lead.metadata?.telegramNick && (
                            <a href={`https://t.me/${String(lead.metadata.telegramNick).replace(/^@/, '')}`} target='_blank' rel='noopener noreferrer' className='oc-lead-card__contact' onClick={e => e.stopPropagation()}>
                                {lead.metadata.telegramNick}
                            </a>
                        )}
                    </div>
                )}

                <span className='oc-lead-card__meta'>
                    Создан {formatDateTime(lead.createdAt)}
                </span>

                {lead.inAirtable && airLinks.length > 0 && (
                    <div className='oc-lead-card__contacts'>
                        {airLinks.map((l) => (
                            <a
                                key={l.recordId}
                                className='oc-lead-card__air-link'
                                href={l.url}
                                target='_blank'
                                rel='noreferrer'
                                title='Запись в Airtable'
                            >
                                Air · team {l.teamNumber} ↗
                            </a>
                        ))}
                    </div>
                )}

                {messagingOn && (<div className='oc-lead-card__actions'>
                    <Button
                        hierarchy='secondary'
                        size='small'
                        leadingIcon={<Icon name='envelope-simple' size='sm' />}
                        onClick={() => onOpenTab?.('email', 'email')}
                    >
                        Написать
                    </Button>
                    <Button
                        hierarchy='secondary'
                        size='small'
                        leadingIcon={<Icon name='instagram-logo' size='sm' />}
                        onClick={() => onOpenTab?.('email', 'ig')}
                    >
                        Direct
                    </Button>
                </div>)}
            </Collapsible>

            {canUseOutreach && (<Collapsible
                id='oc-lead-card-campaign'
                open={open.campaign}
                onToggle={() => toggleBlock('campaign')}
                title='Email-кампания'
                meta={Array.isArray(lead.campaigns) && lead.campaigns.length > 0
                    ? <Badge color='green' type='contained' size='small'>активно</Badge>
                    : null}
            >
                {!Array.isArray(lead.campaigns) || lead.campaigns.length === 0 ? (
                    <div className='oc-lead-card__campaign-empty'>
                        <span className='oc-lead-card__empty-text'>Не в кампании</span>
                    </div>
                ) : (
                    <div className='oc-lead-card__campaign-list'>
                        {lead.campaigns.map(c => (
                            <div key={c.id} className='oc-lead-card__campaign-row'>
                                <CampaignPills campaign={c} />
                                <Button
                                    hierarchy='destructive'
                                    size='small'
                                    iconOnly
                                    title='Удалить из кампании'
                                    onClick={async () => {
                                        if (!lead?.id || !c.id) return;
                                        if (!window.confirm(`Убрать ${lead.name || 'лида'} из рассылки кампании "${c.name || ''}"? Письма прекратятся. Контакт в Reply не удаляется.`)) return;
                                        try {
                                            await removeLeadFromStageOneCampaign(lead.id, c.id);
                                            queryClient.invalidateQueries({ queryKey: ['lead', lead.id] });
                                            queryClient.invalidateQueries({ queryKey: ['leads'] });
                                            queryClient.invalidateQueries({ queryKey: ['campaignLeads'] });
                                        } catch (err) {
                                            alert('Не удалось убрать из рассылки (лид остался в кампании): ' + (err?.data?.error || err?.data?.detail || err?.message || 'неизвестная ошибка'));
                                        }
                                    }}
                                >
                                    <Icon name='x' size='sm' />
                                </Button>
                            </div>
                        ))}
                    </div>
                )}
                {lead.replyContacts && lead.replyContacts.total > 0 && (
                    <div className='oc-lead-card__campaign-meta'>
                        <span>
                            {lead.replyContacts.total} Reply-контакт(ов) в {lead.replyContacts.perTeam.length} команде(ах)
                        </span>
                        {lead.replyContacts.multiTeam && (
                            <Badge className='oc-lead-card__reply-badge' color='blue' type='contained' size='small'>
                                {lead.replyContacts.perTeam.length} команды
                            </Badge>
                        )}
                        {lead.replyContacts.sameTeamDup && (
                            <Badge className='oc-lead-card__reply-badge' color='red' type='contained' size='small' title='Кандидат на дедуп контактов'>
                                дубль-контакт
                            </Badge>
                        )}
                        {lead.replyContacts.perTeam.some(t => t.count > 1) && (
                            <ul className='oc-lead-card__reply-teams'>
                                {lead.replyContacts.perTeam.filter(t => t.count > 1).map((t, i) => (
                                    <li key={t.teamId ?? `null-${i}`} className='oc-lead-card__reply-team--dup'>
                                        {t.teamName || 'Без команды'}: {t.count}
                                    </li>
                                ))}
                            </ul>
                        )}
                        {Array.isArray(lead.replyContacts.contacts) && lead.replyContacts.contacts.length > 0 && (
                            <div className='oc-lead-card__reply-contacts'>
                                {lead.replyContacts.contacts.map((rc) => (
                                    <div key={rc.replyContactId} className='oc-lead-card__reply-contact'>
                                        <span className='oc-lead-card__reply-contact-text'>
                                            {rc.teamName || 'Без команды'} · {rc.campaignName || '— нет кампании'} · {rc.status} · {rc.email}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
                <div className='oc-lead-card__campaign-actions'>
                    <Button
                        hierarchy='secondary'
                        size='small'
                        onClick={async () => {
                            if (!canUseOutreach || !lead?.id || isSyncingCampaigns) return;
                            setIsSyncingCampaigns(true);
                            try {
                                await syncLeadStageOneCampaignMemberships(lead.id);
                                queryClient.invalidateQueries({ queryKey: ['lead', lead.id] });
                                queryClient.invalidateQueries({ queryKey: ['leads'] });
                                queryClient.invalidateQueries({ queryKey: ['campaignLeads'] });
                            } catch (err) {
                                alert('Не удалось синхронизировать: ' + (err?.data?.error || err?.message || 'unknown'));
                            } finally {
                                setIsSyncingCampaigns(false);
                            }
                        }}
                        disabled={isSyncingCampaigns}
                        title='Обновить данные из провайдера'
                    >
                        {isSyncingCampaigns ? '…' : '↻'}
                    </Button>
                    <Button
                        hierarchy='secondary'
                        size='small'
                        onClick={() => setFunnelEnrollModalOpen(true)}
                    >
                        + Добавить
                    </Button>
                </div>
            </Collapsible>)}

            <Collapsible
                id='oc-lead-card-reminders'
                open={open.reminders}
                onToggle={() => toggleBlock('reminders')}
                title='Напоминания · теги'
                meta={
                    <span className='oc-lead-card__acc-meta'>
                        <Icon name='bell-ringing' size='sm' /> {leadReminders.length}
                    </span>
                }
            >
                <ReminderForm
                    leadId={lead?.id}
                    reminders={leadReminders}
                    onSaved={() => queryClient.invalidateQueries({ queryKey: queryKeys.reminders.byLead(lead?.id) })}
                />

                <div className='oc-lead-card__tags-head'>
                    <h3 className='oc-lead-card__block-title'>Теги</h3>
                    <TagPicker
                        value={newTagLabel}
                        onChange={setNewTagLabel}
                        onCommit={async (label) => {
                            if (!label) return;
                            try {
                                await assignTagByLabel(lead.id, label);
                            } catch (err) {
                                console.error('Failed to assign tag', err);
                                return;
                            }
                            setNewTagLabel('');
                            queryClient.invalidateQueries({ queryKey: queryKeys.tags.all });
                            queryClient.invalidateQueries({ queryKey: queryKeys.leads.detail(lead.id) });
                            queryClient.invalidateQueries({ queryKey: queryKeys.leads.all });
                            queryClient.invalidateQueries({ queryKey: ['leads', 'column'] });
                        }}
                        placeholder='+ Добавить тег'
                        compact
                    />
                </div>
                <div className='oc-lead-card__tags'>
                    {(lead.tags || []).map(tag => (
                        <Badge
                            key={tag.id}
                            className='oc-lead-card__tag'
                            color='neutral'
                            type='contained'
                            size='small'
                            style={{ backgroundColor: tag.color }}
                            removable
                            onRemove={async () => {
                                await removeTag(lead.id, tag.id).catch(() => {});
                                queryClient.invalidateQueries({ queryKey: queryKeys.leads.detail(lead.id) });
                                queryClient.invalidateQueries({ queryKey: queryKeys.leads.all });
                                queryClient.invalidateQueries({ queryKey: ['leads', 'column'] });
                            }}
                        >
                            {tag.label}
                        </Badge>
                    ))}
                </div>
            </Collapsible>

            <Collapsible
                id='oc-lead-card-service'
                open={open.service}
                onToggle={() => toggleBlock('service')}
                title='Служебное'
            >
                <div className='oc-lead-card__actions'>
                    {lead.originalPinned && (
                        <span className='oc-lead-card__pin' title='Оригинал закреплён вручную'>
                            <Icon name='push-pin' size='sm' /> закреплён
                        </span>
                    )}
                    {lead.isDuplicate && !lead.originalPinned && (
                        <Button
                            hierarchy='secondary'
                            size='small'
                            title='Сделать этот лид оригиналом блогера'
                            onClick={async () => {
                                if (!lead?.id) return;
                                if (!window.confirm('Сделать этот лид оригиналом блогера? Остальные станут дублями.')) return;
                                try {
                                    await makeLeadOriginal(lead.id, true);
                                    qc.invalidateQueries({ queryKey: queryKeys.leads.detail(lead.id) });
                                    qc.invalidateQueries({ queryKey: ['leads', 'bloggers'] });
                                    qc.invalidateQueries({ queryKey: queryKeys.leads.all });
                                } catch (err) {
                                    alert('Не удалось закрепить оригинал: ' + (err?.data?.error || err?.message || 'неизвестная ошибка'));
                                }
                            }}
                        >
                            Сделать оригиналом
                        </Button>
                    )}
                    {lead.inAirtable === false && !lead.isDuplicate && (
                        <Button
                            hierarchy='destructive'
                            size='small'
                            disabled={isCreatingInAirtable}
                            onClick={async () => {
                                if (!lead?.id || isCreatingInAirtable) return;
                                setIsCreatingInAirtable(true);
                                try {
                                    const result = await createInAirtable(lead.id);
                                    if (result?.ok) {
                                        qc.invalidateQueries({ queryKey: queryKeys.leads.detail(lead.id) });
                                        qc.invalidateQueries({ queryKey: ['leads', 'bloggers'] });
                                    } else {
                                        alert('Ошибка: ' + (result?.error || 'неизвестная ошибка'));
                                    }
                                } catch (err) {
                                    alert('Ошибка при создании в Airtable: ' + (err?.data?.error || err?.message || 'неизвестная ошибка'));
                                } finally {
                                    setIsCreatingInAirtable(false);
                                }
                            }}
                        >
                            {isCreatingInAirtable ? '…' : 'Создать в Air'}
                        </Button>
                    )}
                </div>

                <section className='oc-lead-card__block'>
                    <h3 className='oc-lead-card__block-title'>Амбассадорские аккаунты</h3>
                    {showEditForm ? (
                        <>
                            <div className='oc-lead-card__extras'>
                                {extraAccounts.map((acc, i) => (
                                    <span key={`${acc.platform}-${acc.handle}-${i}`} className='oc-lead-card__extra-chip'>
                                        <span className='oc-lead-card__extra-chip-label'>{PLATFORM_LABEL_SHORT[acc.platform] || acc.platform}</span>
                                        {acc.handle}
                                        <Button
                                            hierarchy='tertiary'
                                            size='small'
                                            iconOnly
                                            className='oc-lead-card__extra-chip-remove'
                                            title={lockedExtras.has(extraKey(acc)) ? HANDLE_LOCK_HINT : 'Убрать аккаунт'}
                                            aria-label='Убрать аккаунт'
                                            disabled={inlineSaving || lockedExtras.has(extraKey(acc))}
                                            onClick={() => removeExtraAccount(i)}
                                        >
                                            <Icon name='x' size='sm' />
                                        </Button>
                                        {lockedExtras.has(extraKey(acc)) && <span className='oc-lead-card__empty-text'>{HANDLE_LOCK_HINT}</span>}
                                    </span>
                                ))}
                                {extraAccounts.length === 0 && !newAccPlatform && (
                                    <span className='oc-lead-card__empty-text'>—</span>
                                )}
                                {extraAccounts.length < 5 && !newAccPlatform && (
                                    <SelectNative
                                        compact
                                        className='oc-lead-card__extra-add'
                                        value=''
                                        disabled={inlineSaving}
                                        aria-label='Добавить амбассадорский аккаунт'
                                        onChange={e => { if (e.target.value) { setNewAccPlatform(e.target.value); setNewAccHandle(''); } }}
                                    >
                                        <option value=''>+</option>
                                        <option value='instagram'>IG</option>
                                        <option value='tiktok'>TT</option>
                                        <option value='youtube'>YT</option>
                                        <option value='facebook'>FB</option>
                                        <option value='threads'>Threads</option>
                                        <option value='x'>X</option>
                                    </SelectNative>
                                )}
                            </div>
                            {newAccPlatform && (
                                <div className='oc-lead-card__extra-row'>
                                    <span className='oc-lead-card__extra-chip-label'>{PLATFORM_LABEL_SHORT[newAccPlatform] || newAccPlatform}</span>
                                    <input
                                        className='oc-lead-card__input'
                                        type='text'
                                        placeholder='@username'
                                        value={newAccHandle}
                                        disabled={inlineSaving}
                                        onChange={e => setNewAccHandle(e.target.value)}
                                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addExtraAccount(); } }}
                                    />
                                    <Button
                                        hierarchy='secondary'
                                        size='small'
                                        disabled={!newAccHandle.trim() || inlineSaving}
                                        onClick={addExtraAccount}
                                    >
                                        Добавить
                                    </Button>
                                    <Button
                                        hierarchy='tertiary'
                                        size='small'
                                        iconOnly
                                        title='Отменить добавление'
                                        aria-label='Отменить добавление'
                                        onClick={() => { setNewAccPlatform(''); setNewAccHandle(''); }}
                                    >
                                        <Icon name='x' size='sm' />
                                    </Button>
                                </div>
                            )}
                        </>
                    ) : extraAccountsList.length > 0 ? (
                        <div className='oc-lead-card__contacts'>
                            {extraAccountsList.map((acc, i) => {
                                const platform = (acc.platform || '').toLowerCase();
                                const colorClass = PLATFORM_LINK_CLASS[platform] || '';
                                const Glyph = PLATFORM_ICONS[platform];
                                const icon = Glyph ? <Glyph /> : null;
                                const href = acc.profileUrl ?? buildProfileUrl(platform, acc.handle);
                                const marker = acc.packId == null
                                    ? <span className='oc-lead-card__muted'>без пачки</span>
                                    : null;
                                return (
                                    <React.Fragment key={`${platform}-${acc.handle}-${i}`}>
                                        {href ? (
                                            <a href={href} target='_blank' rel='noopener noreferrer' className={`oc-lead-card__contact ${colorClass}`} onClick={e => e.stopPropagation()}>
                                                {icon} {acc.handle}
                                            </a>
                                        ) : (
                                            <span className={`oc-lead-card__contact ${colorClass}`}>
                                                {icon} {acc.handle}
                                            </span>
                                        )}
                                        {marker}
                                    </React.Fragment>
                                );
                            })}
                        </div>
                    ) : (
                        <span className='oc-lead-card__empty-text'>—</span>
                    )}
                </section>

                {lead?.aiTag && (
                    <section className='oc-lead-card__block'>
                        <div className='oc-lead-card__status-row'>
                            <div className='oc-lead-card__field'>
                                <span className='oc-lead-card__field-label'>Инбокс</span>
                                <Badge color='purple' type='contained' size='small'>{lead.aiTag}</Badge>
                            </div>
                        </div>
                    </section>
                )}

                {(() => {
                    const current = pickActiveCollab(leadCollabs);
                    if (!current) return null;
                    const symbol = { USD: '$', EUR: '€', RUB: '₽' }[current.currency] || current.currency || '';
                    const statusLabel = COLLAB_STATUS_LABELS[current.status] || current.status;
                    const amount = current.agreedPrice
                        ? `${symbol}${Number(current.agreedPrice).toLocaleString('ru-RU')}`
                        : null;
                    const parts = [
                        `${formatCollabDate(current.startDate)} — ${formatCollabDate(current.endDate)}`,
                        statusLabel,
                        amount,
                    ].filter(Boolean).join(' · ');
                    return (
                        <section className='oc-lead-card__block'>
                            <h3 className='oc-lead-card__block-title'>Коллаборация</h3>
                            <div className='oc-lead-card__row'>
                                <span className='oc-lead-card__value'>{parts}</span>
                            </div>
                        </section>
                    );
                })()}

                {igProfile && (
                    <section className='oc-lead-card__block'>
                        <h3 className='oc-lead-card__block-title'>Профиль Instagram (из парсинга)</h3>
                        <div className='oc-lead-card__ig-profile'>
                            {igProfile.fullName && (
                                <div className='oc-lead-card__ig-field'>
                                    <span className='oc-lead-card__ig-key'>Имя:</span>{igProfile.fullName}
                                </div>
                            )}
                            {igProfile.followerCount && (
                                <div className='oc-lead-card__ig-field'>
                                    <span className='oc-lead-card__ig-key'>Подписчики:</span>
                                    {igProfile.followerCount.toLocaleString()}
                                </div>
                            )}
                            {igProfile.geo && (
                                <div className='oc-lead-card__ig-field'>
                                    <span className='oc-lead-card__ig-key'>Гео:</span>{igProfile.geo}
                                </div>
                            )}
                            {igProfile.bio && (
                                <div className='oc-lead-card__ig-bio'>
                                    {igProfile.bio}
                                </div>
                            )}
                            {igProfile.publicEmail && igProfile.publicEmail !== lead.email && (
                                <div className='oc-lead-card__ig-field'>
                                    <span className='oc-lead-card__ig-key'>Email (IG):</span>{igProfile.publicEmail}
                                </div>
                            )}
                        </div>
                    </section>
                )}
            </Collapsible>

            {showEditForm && (
                <div className='oc-lead-card__editbar'>
                    {saveError && (
                        <div className='oc-lead-card__error oc-lead-card__editbar-error' role='alert'>
                            {saveError}
                        </div>
                    )}
                    <span className='oc-lead-card__editbar-note'>
                        <Icon name='pencil-simple' size='sm' /> Режим редактирования
                    </span>
                    <span className='oc-lead-card__editbar-actions'>
                        <Button
                            hierarchy='secondary'
                            size='small'
                            disabled={inlineSaving}
                            onClick={() => onCancelEdit?.()}
                        >
                            Отмена
                        </Button>
                        <Button
                            hierarchy='primary'
                            size='small'
                            loading={inlineSaving}
                            onClick={handleInlineSave}
                        >
                            Сохранить
                        </Button>
                    </span>
                </div>
            )}

        </aside>
    );
}
